import { useMemo, useState } from 'react';

import { validateEmail } from '~/lib/common';
import {
  Address,
  CustomShippingAddress,
  UserRole,
} from '~/lib/constants/patients.constants';
import { providerStatesEnumMenuOptionsMapper } from '~/lib/util/profile.util';
import { useGetProviderStatesEnumQuery } from '~/store/services/enumerations';

import {
  getIsShippingAddressAllFieldEmptyForBodyTrace,
  getIsShippingAddressFieldEmptyForBodyTrace,
  toPatientAddress,
  validatePhoneNumber,
} from '../util/patient.util';

const useCustomShippingAddress = (
  addressData: Address[],
  selectedAddress?: Address,
) => {
  const defaultAddress =
    addressData.find((address) => address.userRole === UserRole.PROXY) ||
    addressData.find((address) => address.userRole === UserRole.PATIENT);
  const [address, setAddress] = useState<CustomShippingAddress>({
    addressLine1: selectedAddress?.addressLine1 || '',
    addressLine2: selectedAddress?.addressLine2 || '',
    city: selectedAddress?.city || '',
    latitude: 0,
    longitude: 0,
    state: selectedAddress?.state || '',
    zipCode: selectedAddress?.zipCode || '',
    email: selectedAddress?.email || defaultAddress?.email || '',
    userRole: null,
    firstName:
      selectedAddress?.firstName ||
      defaultAddress?.firstName ||
      defaultAddress?.chosenName ||
      '',
    lastName: selectedAddress?.lastName || defaultAddress?.lastName || '',
    phoneNumber:
      selectedAddress?.phoneNumber || defaultAddress?.phoneNumber || '',
    name:
      defaultAddress?.firstName || defaultAddress?.lastName
        ? `${defaultAddress?.firstName || ''} ${defaultAddress?.lastName || ''}`
        : '',
  });

  const { data: providerStatesData, isLoading: isGetStatesLoading } =
    useGetProviderStatesEnumQuery();

  const stateOptions = useMemo(
    () => providerStatesEnumMenuOptionsMapper(providerStatesData),
    [providerStatesData],
  );

  const isEmailValid = address.email ? validateEmail(address.email) : true;
  const isPhoneValid = validatePhoneNumber(address.phoneNumber);

  const handleAddressChange = (
    field: keyof Address,
    value: string | GooglePlacesField,
  ) => {
    if (typeof value === 'string') {
      setAddress((prev) => ({ ...prev, [field]: value }));
    } else {
      const patientAddress = toPatientAddress(value);
      const updatedAddress: Partial<Address> = {
        addressLine1: patientAddress?.addressLine1,
        addressLine2: patientAddress?.addressLine2,
        city: patientAddress?.city,
        state: patientAddress?.state,
        zipCode: patientAddress?.zip,
      };

      setAddress((prev) => ({ ...prev, ...updatedAddress }));
    }
  };

  const isFormValid =
    address.firstName &&
    address.lastName &&
    address.email &&
    address.phoneNumber &&
    isEmailValid &&
    isPhoneValid;
  const isShippingAddressAllFieldEmpty =
    getIsShippingAddressAllFieldEmptyForBodyTrace(address);

  const isShippingAddressFieldEmpty =
    getIsShippingAddressFieldEmptyForBodyTrace(address);

  return {
    address,
    stateOptions,
    isEmailValid,
    isPhoneValid,
    isFormValid,
    isGetStatesLoading,
    isShippingAddressFieldEmpty,
    isShippingAddressAllFieldEmpty,
    handleAddressChange,
  };
};

export default useCustomShippingAddress;
